import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import type { Cliente, Servicio, Empresa, CotizacionItemsEstandar, Seccion, EstadoCotizacion, Moneda, Cotizacion } from '@/lib/types'
import { formatCurrency, nextCotNumber } from '@/lib/utils'

/** Round to 2 decimal places — avoids float precision issues */
const r2 = (n: number) => Math.round(n * 100) / 100

export default function NuevaCotizacion() {
  const db = useSupabase()
  const nav = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  // Reference data
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [servicios, setServicios] = useState<Servicio[]>([])
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Form state
  const [numero, setNumero] = useState('')
  const [clienteId, setClienteId] = useState<number | null>(null)
  const [contacto, setContacto] = useState('')
  const [telefono, setTelefono] = useState('')
  const [ruc, setRuc] = useState('')
  const [email, setEmail] = useState('')
  const [proyecto, setProyecto] = useState('')
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0])
  const [validez, setValidez] = useState(90)
  const [moneda, setMoneda] = useState<Moneda>('USD')
  const [estado, setEstado] = useState<EstadoCotizacion>('borrador')
  const [igvPct, setIgvPct] = useState(18)
  const [descPct, setDescPct] = useState(0)
  const [notas, setNotas] = useState('')
  const [items, setItems] = useState<CotizacionItemsEstandar[]>([])
  const [secciones, setSecciones] = useState<Seccion[]>([])

  // Calculations (derived — no state needed per skill guidance)
  const subtotal = r2(items.reduce((s, i) => s + r2(i.precio_unitario * i.cantidad), 0))
  const igvMonto = r2(subtotal * igvPct / 100)
  const descMonto = r2(subtotal * descPct / 100)
  const total = r2(subtotal + igvMonto - descMonto)

  // Load reference data + editing data
  useEffect(() => {
    (async () => {
      try {
        const [c, s, e, allCot] = await Promise.all([
          db.getAll<Cliente>('clientes'), db.getAll<Servicio>('servicios'),
          db.getAll<Empresa>('empresa'), db.getAll<Cotizacion>('cotizaciones'),
        ])
        setClientes(c); setServicios(s); setEmpresa(e[0] || null)

        if (isEdit) {
          const cot = await db.getById<Cotizacion>('cotizaciones', parseInt(id!))
          if (cot) {
            setNumero(cot.numero); setClienteId(cot.cliente_id); setContacto(cot.cliente_contacto ?? '')
            setTelefono(cot.cliente_telefono ?? ''); setRuc(cot.cliente_ruc ?? ''); setEmail(cot.cliente_email ?? '')
            setProyecto(cot.proyecto_nombre ?? ''); setFecha(cot.fecha_emision ?? ''); setValidez(cot.validez_dias)
            setMoneda(cot.moneda as Moneda); setEstado(cot.estado); setIgvPct(cot.igv_porcentaje); setDescPct(cot.descuento_porcentaje)
            setNotas(cot.notas ?? ''); setItems(Array.isArray(cot.items) ? cot.items as CotizacionItemsEstandar[] : [])
            setSecciones(Array.isArray(cot.secciones) ? cot.secciones : [])
          }
        } else {
          setNumero(nextCotNumber(allCot.map(q => q.numero)))
        }
      } catch { showToast('Error al cargar datos', 'error') }
      finally { setLoading(false) }
    })()
  }, [id])

  // Fill client data on select
  const fillCliente = useCallback((cId: number) => {
    setClienteId(cId)
    const c = clientes.find(x => x.id === cId)
    if (c) { setContacto(c.contacto ?? ''); setRuc(c.ruc_dni ?? ''); setTelefono(c.telefono ?? ''); setEmail(c.email ?? '') }
  }, [clientes])

  // Add service from catalog
  const addServicio = useCallback((sId: number) => {
    const s = servicios.find(x => x.id === sId)
    if (!s) return
    setItems(prev => [...prev, { descripcion: s.nombre, precio_unitario: s.precio_base, cantidad: 1, total: s.precio_base }])
    if (secciones.length === 0 && Array.isArray(s.secciones) && s.secciones.length > 0) {
      setSecciones(s.secciones); showToast(`${s.secciones.length} secciones copiadas`, 'info')
    }
    if (!proyecto) setProyecto(s.nombre)
  }, [servicios, secciones.length, proyecto])

  // Item mutations
  const updateItem = (idx: number, field: keyof CotizacionItemsEstandar, value: string) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it
      const updated = { ...it }
      if (field === 'descripcion') updated.descripcion = value
      else { (updated as unknown as Record<string, number>)[field] = parseFloat(value) || 0 }
      updated.total = r2(updated.precio_unitario * updated.cantidad)
      return updated
    }))
  }
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx))
  const addEmpty = () => setItems(prev => [...prev, { descripcion: '', precio_unitario: 0, cantidad: 1, total: 0 }])

  // Save
  const save = async () => {
    if (items.length === 0) { showToast('Agrega al menos un item', 'error'); return }
    setSaving(true)
    try {
      const clienteNombre = clientes.find(c => c.id === clienteId)?.empresa ?? ''
      const data: Record<string, unknown> = {
        numero, empresa_id: empresa?.id ?? 1, cliente_id: clienteId, cliente_nombre: clienteNombre,
        cliente_contacto: contacto, cliente_telefono: telefono, cliente_ruc: ruc, cliente_email: email,
        fecha_emision: fecha, validez_dias: validez, proyecto_nombre: proyecto,
        items, subtotal, igv_porcentaje: igvPct, igv_monto: igvMonto,
        descuento_porcentaje: descPct, descuento_monto: descMonto, total, moneda, estado,
        metodos_pago: ['Transferencia bancaria - clientes Perú', 'Tarjeta de débito/crédito - internacional', 'Criptomonedas - internacional'],
        secciones, notas, tipo_cotizacion: 'estandar',
      }
      if (isEdit) { data.id = parseInt(id!); await db.update('cotizaciones', data); showToast('Cotización actualizada', 'success') }
      else { await db.add('cotizaciones', data); showToast('Cotización creada', 'success') }
      nav('/cotizaciones')
    } catch (err) { showToast(err instanceof Error ? err.message : 'Error al guardar', 'error') }
    finally { setSaving(false) }
  }

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><div className="page-header"><h1>{isEdit ? 'Editar' : 'Nueva'} Cotización</h1></div><div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  return (
    <div className="page-enter">
      <div className="page-header">
        <div><h1>{isEdit ? 'Editar' : 'Nueva'} Cotización</h1><p className="page-subtitle">N° {numero}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => nav('/cotizaciones')}>Cancelar</button>
          <button className="btn btn-success" onClick={save} disabled={saving}>{saving ? 'Guardando...' : '💾 Guardar'}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* LEFT COLUMN */}
        <div>
          {/* Client */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>Datos del Cliente</h3></div>
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <select className="form-select" value={clienteId ?? ''} onChange={e => fillCliente(parseInt(e.target.value))}>
                <option value="">— Seleccionar cliente —</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Contacto</label><input className="form-input" value={contacto} onChange={e => setContacto(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">RUC/DNI</label><input className="form-input" value={ruc} onChange={e => setRuc(e.target.value)} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={telefono} onChange={e => setTelefono(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={email} onChange={e => setEmail(e.target.value)} /></div>
            </div>
          </div>
          {/* Project */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>Proyecto</h3></div>
            <div className="form-group"><label className="form-label">Nombre</label><input className="form-input" value={proyecto} onChange={e => setProyecto(e.target.value)} placeholder="Ej: Diagnóstico Comercial" /></div>
          </div>
          {/* Conditions */}
          <div className="card">
            <div className="card-header"><h3>Condiciones</h3></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Fecha</label><input className="form-input" type="date" value={fecha} onChange={e => setFecha(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Validez (días)</label><input className="form-input" type="number" value={validez} onChange={e => setValidez(parseInt(e.target.value) || 90)} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Moneda</label><select className="form-select" value={moneda} onChange={e => setMoneda(e.target.value as Moneda)}><option value="USD">USD</option><option value="PEN">PEN</option><option value="EUR">EUR</option></select></div>
              <div className="form-group"><label className="form-label">Estado</label><select className="form-select" value={estado} onChange={e => setEstado(e.target.value as EstadoCotizacion)}><option value="borrador">Borrador</option><option value="enviada">Enviada</option><option value="aceptada">Aceptada</option><option value="rechazada">Rechazada</option></select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">IGV %</label><input className="form-input" type="number" value={igvPct} onChange={e => setIgvPct(parseFloat(e.target.value) || 0)} /></div>
              <div className="form-group"><label className="form-label">Descuento %</label><input className="form-input" type="number" value={descPct} onChange={e => setDescPct(parseFloat(e.target.value) || 0)} /></div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div>
          {/* Items */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <h3>Items / Servicios</h3>
              <div style={{ display: 'flex', gap: 6 }}>
                <select className="form-select" id="add-srv" style={{ width: 'auto', padding: '6px 30px 6px 10px', fontSize: '0.82rem' }} defaultValue="">
                  <option value="">+ Agregar servicio</option>
                  {servicios.map(s => <option key={s.id} value={s.id}>{s.nombre} ({formatCurrency(s.precio_base, s.moneda)})</option>)}
                </select>
                <button className="btn btn-primary btn-sm" onClick={() => { const el = document.getElementById('add-srv') as HTMLSelectElement; const v = parseInt(el?.value); if (v) { addServicio(v); el.value = '' } }}>Agregar</button>
              </div>
            </div>
            <div className="items-editor">
              <div className="item-row" style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                <span>Descripción</span><span>P. Unit.</span><span>Cant.</span><span>Total</span><span></span>
              </div>
              {items.map((it, i) => (
                <div className="item-row" key={i}>
                  <input className="form-input" value={it.descripcion} onChange={e => updateItem(i, 'descripcion', e.target.value)} />
                  <input className="form-input" type="number" step="0.01" value={it.precio_unitario} onChange={e => updateItem(i, 'precio_unitario', e.target.value)} />
                  <input className="form-input" type="number" min="1" value={it.cantidad} onChange={e => updateItem(i, 'cantidad', e.target.value)} />
                  <input className="form-input" value={r2(it.precio_unitario * it.cantidad).toFixed(2)} readOnly style={{ opacity: 0.7 }} />
                  <button className="remove-item" onClick={() => removeItem(i)}>✕</button>
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={addEmpty}>+ Item manual</button>
            </div>
            {/* Totals */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem' }}><span>Subtotal</span><strong>{subtotal.toFixed(2)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem' }}><span>IGV ({igvPct}%)</span><strong>{igvMonto.toFixed(2)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem' }}><span>Descuento</span><strong>-{descMonto.toFixed(2)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '1.1rem', color: 'var(--success)', borderTop: '1px solid var(--border)', marginTop: 4 }}><span style={{ fontWeight: 700 }}>TOTAL</span><strong>{total.toFixed(2)} {moneda}</strong></div>
            </div>
          </div>
          {/* Notes */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><h3>Notas</h3></div>
            <textarea className="form-textarea" value={notas} onChange={e => setNotas(e.target.value)} placeholder="Notas adicionales..." />
          </div>
          {/* Sections */}
          <div className="card">
            <div className="card-header"><h3>Secciones de Propuesta</h3><span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{secciones.length} sección(es)</span></div>
            {secciones.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '8px 0' }}>Sin secciones. Agrega un servicio para cargar su plantilla.</p> : (
              secciones.map((sec, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: '0.85rem', flex: 1 }}><strong>{i + 1}.</strong> {sec.titulo}</span>
                  <button className="btn btn-sm" style={{ color: 'var(--danger)', background: 'none', border: '1px solid var(--border)', padding: '4px 8px', fontSize: '0.75rem' }} onClick={() => setSecciones(prev => prev.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
